import { Trash2 } from 'lucide-react';
import { useState } from 'react';
import { dateSortKey, formatDate } from '../../model/dates';
import { other } from '../../model/graph';
import { displayName, eventTitle, shortName } from '../../model/people';
import { citationsAt, setCitations, sourceLabel, type CiteTarget } from '../../model/research';
import type { Citation, CitationQuality, Id, Relationship, Tree } from '../../model/types';
import { PARENT_TYPE_LABELS, PARTNER_TYPE_LABELS, QUALITY_LABELS, SIBLING_TYPE_LABELS, SOCIAL_TYPE_LABELS } from '../labels';
import { closeDialog, commit, getState, openDialog, useApp } from '../store';
import { Field, Modal } from './common';

export function targetKey(t: CiteTarget): string {
  return 'relId' in t ? `r:${t.relId}` : t.eventId ? `e:${t.personId}:${t.eventId}` : `p:${t.personId}`;
}

/** How a relationship looks from one person's side: "Partner: Bo", "Parent: Ada". */
function relLabel(tree: Tree, r: Relationship, from: Id): string {
  const o = tree.people[other(r, from)];
  const name = o ? shortName(o) : 'someone';
  if (r.kind === 'parent') return `${r.a === from ? 'Child' : 'Parent'}: ${name}${r.type !== 'biological' ? ` (${PARENT_TYPE_LABELS[r.type].toLowerCase()})` : ''}`;
  if (r.kind === 'partner') return `${PARTNER_TYPE_LABELS[r.type]}: ${name}`;
  if (r.kind === 'sibling') return `Sibling: ${name}${r.type !== 'unknown' && r.type !== 'full' ? ` (${SIBLING_TYPE_LABELS[r.type].toLowerCase()})` : ''}`;
  return `${r.label || SOCIAL_TYPE_LABELS[r.type]}: ${name}`;
}

/** What a citation supports, in words, from `from`'s point of view (or the target person's). */
export function targetLabel(tree: Tree, t: CiteTarget, from?: Id): string {
  if ('relId' in t) {
    const r = tree.relationships[t.relId];
    if (!r) return 'A removed relationship';
    if (from && (r.a === from || r.b === from)) return relLabel(tree, r, from);
    const a = tree.people[r.a];
    const b = tree.people[r.b];
    const kind = r.kind === 'parent' ? 'parent and child' : r.kind === 'partner' ? PARTNER_TYPE_LABELS[r.type].toLowerCase() : r.kind === 'sibling' ? 'siblings' : (r.label || SOCIAL_TYPE_LABELS[r.type]).toLowerCase();
    return `${a ? displayName(a) : '?'} and ${b ? displayName(b) : '?'}: ${kind}`;
  }
  const p = tree.people[t.personId];
  if (!p) return 'A removed person';
  const who = from === t.personId ? '' : displayName(p);
  if (!t.eventId) return who || 'About them in general';
  const e = p.events.find((x) => x.id === t.eventId);
  if (!e) return who || 'A removed event';
  const what = [eventTitle(e), formatDate(e.date)].filter(Boolean).join(', ');
  return who ? `${who}: ${what}` : what;
}

/** The things about a person a citation can support. */
function targetsFor(tree: Tree, personId: Id): { target: CiteTarget; label: string }[] {
  const p = tree.people[personId];
  if (!p) return [];
  const out: { target: CiteTarget; label: string }[] = [{ target: { personId }, label: `${shortName(p)} in general` }];
  for (const e of [...p.events].sort((a, b) => dateSortKey(a.date) - dateSortKey(b.date))) out.push({ target: { personId, eventId: e.id }, label: targetLabel(tree, { personId, eventId: e.id }, personId) });
  for (const r of Object.values(tree.relationships)) if (r.a === personId || r.b === personId) out.push({ target: { relId: r.id }, label: relLabel(tree, r, personId) });
  return out;
}

/** Cite a source for something about a person, or edit or remove an existing citation. */
export function CitationEditor({ personId, target, index, sourceId }: { personId: Id; target?: CiteTarget; index?: number; sourceId?: Id }) {
  const tree = useApp((s) => s.tree)!;
  const editing = target && index !== undefined ? citationsAt(tree, target)[index] : undefined;
  const [where, setWhere] = useState<CiteTarget>(target ?? { personId });
  const [c, setC] = useState<Citation>(() => editing ?? { sourceId: sourceId ?? '' });
  const options = targetsFor(tree, personId);
  const sources = Object.values(tree.sources).sort((a, b) => a.title.localeCompare(b.title));
  const set = (patch: Partial<Citation>) => setC((x) => ({ ...x, ...patch }));

  const save = () => {
    if (!c.sourceId) return;
    let t = getState().tree!;
    const clean: Citation = { sourceId: c.sourceId };
    if (c.page?.trim()) clean.page = c.page.trim();
    if (c.quality !== undefined) clean.quality = c.quality;
    if (c.note?.trim()) clean.note = c.note.trim();
    if (editing && target && index !== undefined) {
      const list = citationsAt(t, target).filter((_, i) => i !== index);
      if (targetKey(target) === targetKey(where)) {
        list.splice(index, 0, clean);
        t = setCitations(t, target, list);
      } else {
        t = setCitations(t, target, list);
        t = setCitations(t, where, [...citationsAt(t, where), clean]);
      }
    } else t = setCitations(t, where, [...citationsAt(t, where), clean]);
    commit(t);
    closeDialog();
  };

  return (
    <Modal
      title={editing ? 'Edit citation' : 'Cite a source'}
      footer={
        <>
          {editing && target && index !== undefined && (
            <button
              className="btn danger left"
              onClick={() => {
                const t = getState().tree!;
                commit(setCitations(t, target, citationsAt(t, target).filter((_, i) => i !== index)));
                closeDialog();
              }}
            >
              <Trash2 size={15} /> Remove citation
            </button>
          )}
          <button className="btn" onClick={closeDialog}>
            Cancel
          </button>
          <button className="btn primary" onClick={save} disabled={!c.sourceId}>
            Save
          </button>
        </>
      }
    >
      <div className="stack">
        <Field label="What it supports">
          <select className="select" value={targetKey(where)} onChange={(e) => setWhere(options.find((o) => targetKey(o.target) === e.target.value)!.target)}>
            {options.map((o) => (
              <option key={targetKey(o.target)} value={targetKey(o.target)}>
                {o.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Source">
          <div className="row">
            <select className="select grow" value={c.sourceId} onChange={(e) => set({ sourceId: e.target.value })} aria-label="Source">
              <option value="">Choose a source…</option>
              {sources.map((s) => (
                <option key={s.id} value={s.id}>
                  {sourceLabel(s)}
                </option>
              ))}
            </select>
            <button className="btn" onClick={() => openDialog({ type: 'edit-source', sourceId: null, onSave: (id) => set({ sourceId: id }) })}>
              New source
            </button>
          </div>
        </Field>
        <div className="grid-2">
          <Field label="Where in the source" hint="Page, entry, line, or web address">
            <input className="input" value={c.page ?? ''} onChange={(e) => set({ page: e.target.value })} placeholder="e.g., p. 12, entry 34" />
          </Field>
          <Field label="How reliable">
            <select className="select" value={c.quality ?? ''} onChange={(e) => set({ quality: e.target.value === '' ? undefined : (Number(e.target.value) as CitationQuality) })}>
              <option value="">Not rated</option>
              {([3, 2, 1, 0] as CitationQuality[]).map((q) => (
                <option key={q} value={q}>
                  {QUALITY_LABELS[q]}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <Field label="Note">
          <textarea className="textarea" style={{ minHeight: 60 }} value={c.note ?? ''} onChange={(e) => set({ note: e.target.value })} placeholder="What the source says, or why it matters" />
        </Field>
      </div>
    </Modal>
  );
}
